/**
 * Step 3 -- Kimble dump ×3 (f10.6), rendered inside f10.3's step frame through
 * `STEP_BODIES`. Follows `cycle-step.html`, once per scope tab:
 *
 *   upload -> process -> what did not match -> decide -> resulting lines -> accept
 *
 * The tabs are the server's scope list (a lookup), each with its committed
 * badge. A tab shows its newest batch that has not been discarded. Upload and
 * commit each answer `202` with a job watched through the step's job route.
 */

import { useEffect, useRef, useState } from 'react';
import { Check, CheckCircle2, X } from 'lucide-react';

import type { CycleWorkflowStep, KimbleReview, KimbleScope } from '@/api/client';
import {
  isJobTerminal,
  useBusinessUnits,
  useCommitKimbleDumpBatch,
  useDiscardKimbleDumpBatch,
  useKimbleDumpBatches,
  useKimbleDumpJob,
  useKimbleDumpReview,
  useKimbleDumpScopes,
  useRefreshKimbleDump,
  useUploadKimbleDumpBatch,
} from '@/api/queries';
import { JobProgress } from '@/jobs/JobProgress';

import { KimbleDumpCommitDialog } from './KimbleDumpCommitDialog';
import { KimbleDumpDropzone, KimbleDumpFileCard, KimbleDumpProcessingCard } from './KimbleDumpFileCards';
import { KimbleDumpLinesCard } from './KimbleDumpLinesCard';
import { UnmatchedKimbleCard } from './UnmatchedKimbleCard';
import { FIXED_UNIT, hasStagedItems, isParsing, refusalMessage } from './kimbleDumpView';

interface WatchedJob {
  id: string;
  kind: 'parse' | 'commit';
}

export function KimbleDumpStep({ cycleId }: { cycleId: string; step: CycleWorkflowStep }) {
  const scopes = useKimbleDumpScopes(cycleId);
  const [active, setActive] = useState<string | null>(null);

  if (scopes.isPending) return <p className="hint">Loading step 3…</p>;
  if (scopes.isError) {
    return <ErrorAlert message={refusalMessage(scopes.error) ?? 'Step 3 could not be loaded.'} />;
  }
  const list = scopes.data.scopes;
  const current = list.find((scope) => scope.code === active) ?? list[0];
  if (!current) return <p className="hint">No Kimble dump scope is configured.</p>;

  return (
    <div className="stack">
      <div className="tabs" role="tablist" aria-label="Kimble dump scopes">
        {list.map((scope) => (
          <button
            key={scope.code}
            type="button"
            role="tab"
            aria-selected={scope.code === current.code}
            className={`tab${scope.code === current.code ? ' is-active' : ''}`}
            onClick={() => setActive(scope.code)}
          >
            {scope.current_batch_id ? (
              <CheckCircle2 aria-label="Committed" className="us-eng-ok" />
            ) : null}
            {scope.name}
            <span className="count">{scope.current_batch_id ? 'Committed' : 'Pending'}</span>
          </button>
        ))}
      </div>
      <ScopePanel key={current.code} cycleId={cycleId} scope={current} />
    </div>
  );
}

function ScopePanel({ cycleId, scope }: { cycleId: string; scope: KimbleScope }) {
  const batches = useKimbleDumpBatches(cycleId);
  const upload = useUploadKimbleDumpBatch(cycleId);
  const fixedUnit = scope.unit_binding === FIXED_UNIT;
  const units = useBusinessUnits({ enabled: fixedUnit });
  const [unitId, setUnitId] = useState('');
  const [watched, setWatched] = useState<WatchedJob | null>(null);

  const shown = batches.data?.batches.find(
    (batch) => batch.scope?.code === scope.code && batch.state.code !== 'DISCARDED',
  );
  const parsing = shown ? isParsing(shown.state.code) : false;
  const review = useKimbleDumpReview(cycleId, shown && !parsing ? shown.id : undefined);
  const job = useKimbleDumpJob(cycleId, watched?.id);
  useJobCompletion(cycleId, watched, job.data?.state);

  const chosenUnit = unitId || shown?.business_unit?.id || '';
  const startUpload = (file: File) =>
    upload.mutate(
      { file, scope: scope.code, businessUnitId: fixedUnit ? chosenUnit : undefined },
      { onSuccess: (accepted) => setWatched({ id: accepted.job_id, kind: 'parse' }) },
    );

  if (batches.isPending) return <p className="hint">Loading {scope.name}…</p>;
  if (batches.isError) return <ErrorAlert message={refusalMessage(batches.error)} />;

  const unitOptions = fixedUnit
    ? (units.data?.business_units ?? [])
        .filter((unit) => unit.status.code === 'ACTIVE')
        .map((unit) => ({ id: unit.id, name: unit.name }))
    : undefined;

  const jobCard = job.data && (!isJobTerminal(job.data.state) || job.data.state === 'FAILED') && (
    <div className="card">
      <div className="card-body">
        <JobProgress
          job={job.data}
          title={watched?.kind === 'commit' ? 'Writing forecast lines' : `Reading the ${scope.name} dump`}
        />
      </div>
    </div>
  );

  return (
    <div className="stack">
      {upload.error ? <ErrorAlert message={refusalMessage(upload.error)} /> : null}
      {jobCard}

      {!shown && (
        <KimbleDumpDropzone
          scopeName={scope.name}
          onFile={startUpload}
          busy={upload.isPending}
          unitOptions={unitOptions}
          unitId={chosenUnit}
          onUnitChange={setUnitId}
        />
      )}

      {shown && parsing && !jobCard && (
        <p className="hint" role="status">
          Reading {shown.original_filename ?? 'the uploaded file'}…
        </p>
      )}

      {review.isError && <ErrorAlert message={refusalMessage(review.error)} />}

      {review.data && (
        <>
          <KimbleDumpFileCard review={review.data} onReplace={startUpload} busy={upload.isPending} />
          <KimbleDumpProcessingCard review={review.data} />
          {hasStagedItems(review.data.batch.state.code) && (
            <BatchDecisions
              cycleId={cycleId}
              review={review.data}
              onCommitStarted={(jobId) => setWatched({ id: jobId, kind: 'commit' })}
            />
          )}
          {review.data.batch.state.code === 'COMMITTED' && <KimbleDumpLinesCard figures={review.data.figures} />}
        </>
      )}
    </div>
  );
}

/** Refetch once a watched job reaches a terminal state -- once per job. */
function useJobCompletion(cycleId: string, watched: WatchedJob | null, state: string | undefined) {
  const refresh = useRefreshKimbleDump(cycleId);
  const handled = useRef<string | null>(null);

  useEffect(() => {
    if (!watched || !isJobTerminal(state) || handled.current === watched.id) return;
    handled.current = watched.id;
    refresh(watched.kind === 'commit' && state === 'SUCCEEDED');
  }, [watched, state, refresh]);
}

function BatchDecisions({
  cycleId,
  review,
  onCommitStarted,
}: {
  cycleId: string;
  review: KimbleReview;
  onCommitStarted: (jobId: string) => void;
}) {
  const commit = useCommitKimbleDumpBatch(cycleId, review.batch.id);
  const discard = useDiscardKimbleDumpBatch(cycleId, review.batch.id);
  const [confirming, setConfirming] = useState(false);
  const { capabilities } = review;

  const footer = (capabilities.can_commit || capabilities.can_discard) && (
    <div className="card-foot">
      <span className="hint">
        {capabilities.can_commit
          ? 'Accepting writes this scope’s forecast lines with their Kimble defaults.'
          : 'Map or exclude every unresolved unit above to enable Accept and commit.'}
      </span>
      <div className="btn-row">
        {capabilities.can_discard && (
          <button
            type="button"
            className="btn btn-secondary"
            disabled={discard.isPending}
            onClick={() => discard.mutate()}
          >
            <X aria-hidden="true" /> Discard batch
          </button>
        )}
        <button
          type="button"
          className="btn btn-primary"
          disabled={!capabilities.can_commit}
          onClick={() => setConfirming(true)}
        >
          <Check aria-hidden="true" /> Accept and commit
        </button>
      </div>
    </div>
  );

  return (
    <>
      <UnmatchedKimbleCard cycleId={cycleId} review={review} />
      {discard.error ? <ErrorAlert message={refusalMessage(discard.error)} /> : null}
      <KimbleDumpLinesCard figures={review.figures} footer={footer || undefined} />
      {confirming && (
        <KimbleDumpCommitDialog
          review={review}
          busy={commit.isPending}
          error={commit.error}
          onClose={() => {
            setConfirming(false);
            commit.reset();
          }}
          onConfirm={() =>
            commit.mutate(undefined, {
              onSuccess: (accepted) => {
                setConfirming(false);
                onCommitStarted(accepted.job_id);
              },
            })
          }
        />
      )}
    </>
  );
}

function ErrorAlert({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="alert alert-danger" role="alert">
      <div>
        <div className="a-body">{message}</div>
      </div>
    </div>
  );
}
